/* ==========================================================================
   ToTheLove — Avis Judge.me : dates relatives
   --------------------------------------------------------------------------
   Judge.me affiche des dates absolues au format US (07/26/2026). On les
   remplace par du relatif localisé (« il y a 2 semaines »).

   PARTI PRIS
   - Zéro clé i18n : on utilise Intl.RelativeTimeFormat avec la langue du
     document. Le français sort nativement (« il y a 2 semaines »), l'anglais
     aussi le jour où Shopify Markets sera branché.
   - Source de vérité = le JSON exposé par Judge.me
     (jdgm.data.reviewWidget[productId]), apparié aux cartes par l'attribut
     review-uuid. On ne devine rien à partir du texte affiché.
   - Repli sur le parsing MM/JJ/AAAA pour les avis chargés après coup via
     « Afficher plus », qui n'alimentent pas forcément le JSON initial.
   - Judge.me est une app Vue 3 : on n'ajoute et ne supprime AUCUN noeud.
     On réécrit uniquement le texte d'un noeud feuille, ce que Vue sait
     réconcilier sans casse (contrairement à une insertion d'élément —
     cf. le bug Recharge sur firstChild).
   - Idempotent : chaque noeud traité est marqué, et on mémorise le texte
     d'origine pour ne jamais recalculer à partir d'un résultat déjà relatif.
   ========================================================================== */

(function () {
  'use strict';

  var MINUTE = 60000;
  var HOUR = 3600000;
  var DAY = 86400000;
  var WEEK = DAY * 7;
  var MONTH = DAY * 30.44;
  var YEAR = DAY * 365.25;

  class TtlReviews extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-ttl-relative-dates')) return;

      var lang = document.documentElement.lang || 'fr';
      try {
        this.formatter = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
      } catch (e) {
        // Navigateur sans Intl.RelativeTimeFormat : on laisse les dates natives
        return;
      }

      this.dateMap = this.buildDateMap();
      this.pending = null;

      this.apply();

      // Le widget est injecté en asynchrone, puis re-rendu à chaque tri,
      // filtre ou « Afficher plus » : on reste à l'écoute.
      this.observer = new MutationObserver(this.schedule.bind(this));
      this.observer.observe(this, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.pending) cancelAnimationFrame(this.pending);
    }

    schedule() {
      if (this.pending) return;
      this.pending = requestAnimationFrame(
        function () {
          this.pending = null;
          this.apply();
        }.bind(this)
      );
    }

    /* uuid -> date ISO, depuis le JSON déposé par Judge.me */
    buildDateMap() {
      var map = {};
      var productId = this.getAttribute('data-ttl-product-id');
      var widgetData =
        window.jdgm &&
        window.jdgm.data &&
        window.jdgm.data.reviewWidget &&
        window.jdgm.data.reviewWidget[productId];

      if (!widgetData || !Array.isArray(widgetData.reviews)) return map;

      widgetData.reviews.forEach(function (review) {
        if (review && review.uuid && review.created_at) {
          map[review.uuid] = review.created_at;
        }
      });

      return map;
    }

    apply() {
      var items = this.querySelectorAll('.jm-review-item[review-uuid]');
      if (!items.length) return;

      Array.prototype.forEach.call(
        items,
        function (item) {
          var details = item.querySelector('.jm-reviewer-info__details');
          if (!details) return;

          // La date est le seul <span class="jm-text"> enfant DIRECT du bloc
          // de détails (le nom, lui, vit dans .jm-reviewer-info__name-cluster).
          var node = details.querySelector(':scope > span.jm-text');
          if (!node) return;

          var raw = node.getAttribute('data-ttl-date-raw');

          if (raw === null) {
            raw = node.textContent.trim();
            if (!raw) return;
            node.setAttribute('data-ttl-date-raw', raw);
          }

          var iso = this.dateMap[item.getAttribute('review-uuid')];
          var date = iso ? new Date(iso) : this.parseUsDate(raw);
          if (!date || isNaN(date.getTime())) return;

          var relative = this.formatRelative(date);
          if (!relative || node.textContent === relative) return;

          node.textContent = relative;
          // La date exacte reste accessible au survol et aux lecteurs d'écran
          node.setAttribute('title', raw);
        }.bind(this)
      );
    }

    /* Repli : « MM/JJ/AAAA » tel que rendu par Judge.me */
    parseUsDate(text) {
      var match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
      if (!match) return null;
      return new Date(
        Number(match[3]),
        Number(match[1]) - 1,
        Number(match[2])
      );
    }

    formatRelative(date) {
      var diff = date.getTime() - Date.now(); // négatif = passé
      var abs = Math.abs(diff);

      if (abs < HOUR) return this.formatter.format(Math.round(diff / MINUTE), 'minute');
      if (abs < DAY) return this.formatter.format(Math.round(diff / HOUR), 'hour');
      if (abs < WEEK) return this.formatter.format(Math.round(diff / DAY), 'day');
      if (abs < MONTH) return this.formatter.format(Math.round(diff / WEEK), 'week');
      if (abs < YEAR) return this.formatter.format(Math.round(diff / MONTH), 'month');
      return this.formatter.format(Math.round(diff / YEAR), 'year');
    }
  }

  if (!customElements.get('ttl-reviews')) {
    customElements.define('ttl-reviews', TtlReviews);
  }
})();